import { c as _c } from "react/compiler-runtime";
import { Clock, Layers, BookOpen, Calendar, Tag, ExternalLink, Lightbulb, AlertTriangle, CheckCircle2, Flag, Target, Users, HelpCircle, BarChart3, Handshake, ChevronDown, ChevronRight, Bookmark } from 'lucide-react';
import type { Workflow, WorkflowComponent, WorkflowView, WorkflowSource, WorkflowChildEntry, WorkflowComponentRef } from '@sizzlenz/shared';
import { Markdown } from '@/components/Markdown';
import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useComponentTracking } from '@/lib/hooks/useComponentTracking';
import { TrackComponentButton } from '@/components/tracking/TrackComponentButton';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Visual style variants for the workflow detail view */
export type WorkflowStyleVariant = 'default' | 'infographic';

/** Controls initial collapse state of cards */
export interface CollapseConfig {
  /** Default collapse state for all items. Default: false (expanded) */
  defaultCollapsed?: boolean;
  /** Override collapse state for specific component IDs */
  componentOverrides?: Record<string, boolean>;
}

/** Position of the phase label relative to the card title */
export type PhaseLabelPosition = 'above' | 'below';
interface WorkflowDetailViewProps {
  workflow: Workflow;
  /** When true, skips the header (title/tags) and removes outer padding — used when embedded */
  embedded?: boolean;
  /** When true, renders phase components in the timeline. Default: false (phases are hidden, only their children shown). */
  showPhases?: boolean;
  /** When true, shows source links on each component card. Default: false. */
  showComponentSources?: boolean;
  /** When true, shows the sidebar/summary panel. Default: false. */
  showSidebar?: boolean;
  /** Visual style variant. Default: 'default' */
  variant?: WorkflowStyleVariant;
  /** Controls collapse/expand behavior of stage cards */
  collapseConfig?: CollapseConfig;
  /** When true, always renders the single-column (mobile) layout regardless of viewport. Default: false. */
  forceMobileLayout?: boolean;
  /** Show the parent phase label on infographic cards. Default: false. */
  showPhaseLabel?: boolean;
  /** Position of the phase label relative to the title. Default: 'above'. */
  phaseLabelPosition?: PhaseLabelPosition;
  /** ID of the parent object (e.g. content ID) for component tracking. When provided, enables tracking. */
  trackingObjectId?: number;
  /** Type of the parent object for component tracking (e.g. 'content'). Required if trackingObjectId is set. */
  trackingObjectType?: string;
  /** Show the tracking key/legend above the timeline. Default: true when tracking is enabled. */
  showTrackingKey?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isComponentRef(entry: WorkflowChildEntry): entry is WorkflowComponentRef {
  return 'componentId' in entry && !('kind' in entry);
}
function isInlineComponent(entry: WorkflowChildEntry): entry is WorkflowComponent {
  return 'kind' in entry;
}
function resolveComponent(ref: WorkflowComponentRef, components: WorkflowComponent[]): WorkflowComponent | undefined {
  return components.find(c => c.id === ref.componentId);
}
function getKindIcon(kind: string) {
  switch (kind) {
    case 'lesson':
      return <Lightbulb className="w-4 h-4 text-amber-500" />;
    case 'challenge':
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    case 'outcome':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'decision':
      return <Flag className="w-4 h-4 text-blue-500" />;
    case 'insight':
      return <Target className="w-4 h-4 text-purple-500" />;
    case 'metric':
      return <BarChart3 className="w-4 h-4 text-teal-500" />;
    case 'activity':
      return <Target className="w-4 h-4 text-gray-500" />;
    case 'support':
      return <Users className="w-4 h-4 text-indigo-500" />;
    case 'partnership':
      return <Handshake className="w-4 h-4 text-pink-500" />;
    default:
      return <HelpCircle className="w-4 h-4 text-gray-400" />;
  }
}
function getKindColor(kind: string) {
  switch (kind) {
    case 'lesson':
      return 'bg-amber-50 border-amber-100 text-amber-800';
    case 'challenge':
      return 'bg-red-50 border-red-100 text-red-800';
    case 'outcome':
      return 'bg-green-50 border-green-100 text-green-800';
    case 'decision':
      return 'bg-blue-50 border-blue-100 text-blue-800';
    case 'insight':
      return 'bg-purple-50 border-purple-100 text-purple-800';
    case 'metric':
      return 'bg-teal-50 border-teal-100 text-teal-800';
    case 'activity':
      return 'bg-gray-50 border-gray-200 text-gray-800';
    case 'support':
      return 'bg-indigo-50 border-indigo-100 text-indigo-800';
    case 'partnership':
      return 'bg-pink-50 border-pink-100 text-pink-800';
    default:
      return 'bg-gray-50 border-gray-100 text-gray-700';
  }
}
function getInitialCollapsed(componentId: string, config?: CollapseConfig): boolean {
  if (!config) return false;
  if (config.componentOverrides?.[componentId] !== undefined) {
    return config.componentOverrides[componentId];
  }
  return config.defaultCollapsed ?? false;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function WorkflowSidebar(t0) {
  const $ = _c(16);
  const {
    workflow
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h4 className="text-lg font-bold mb-6 text-gray-900">Summary</h4>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== workflow.dates) {
    t2 = workflow.dates?.displayDate && <div className="space-y-2"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block">Period</span><div className="flex items-center gap-2 text-gray-700"><Calendar className="w-4 h-4 text-primary-600" /><span className="text-sm font-bold">{workflow.dates.displayDate}</span></div></div>;
    $[1] = workflow.dates;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ExternalLink className="w-3 h-3" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== workflow.sources.length) {
    t4 = <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">{t3} Sources ({workflow.sources.length})</span>;
    $[4] = workflow.sources.length;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== workflow.sources) {
    t5 = workflow.sources.map(_temp);
    $[6] = workflow.sources;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <div className="space-y-1.5">{t5}</div>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = <div className="space-y-3">{t4}{t6}</div>;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t7) {
    t8 = <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm sticky top-24">{t1}<div className="space-y-6">{t2}{t7}</div></div>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp(source) {
  return <div key={source.id}>{source.url ? <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline font-medium block truncate cursor-pointer" title={source.title}>{source.title}</a> : <span className="text-xs text-gray-600 font-medium block truncate">{source.title}</span>}</div>;
}
function InlineChildComponent(t0) {
  const $ = _c(21);
  const {
    component,
    isTracked,
    onToggleTracking,
    isToggling,
    trackingEnabled
  } = t0;
  let t1;
  if ($[0] !== component.kind) {
    t1 = getKindColor(component.kind);
    $[0] = component.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `flex items-start gap-2 px-3 py-2 rounded-lg border ${t1}`;
  let t3;
  if ($[2] !== component.kind) {
    t3 = <div className="flex items-center gap-2"><span className="text-[10px] font-bold uppercase tracking-widest opacity-60">{component.kind}</span></div>;
    $[2] = component.kind;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== component.name) {
    t4 = <p className="text-sm font-semibold">{component.name}</p>;
    $[4] = component.name;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== component.content) {
    t5 = component.content && <div className="mt-0.5 text-xs opacity-80"><Markdown className="text-xs [&_p]:text-xs [&_li]:text-xs [&_p]:mb-1">{component.content}</Markdown></div>;
    $[6] = component.content;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <div className="min-w-0 flex-1">{t3}{t4}{t5}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isToggling || $[13] !== isTracked || $[14] !== onToggleTracking || $[15] !== trackingEnabled) {
    t7 = trackingEnabled && onToggleTracking && <TrackComponentButton isTracked={!!isTracked} onToggle={onToggleTracking} isToggling={isToggling} size="sm" className="flex-shrink-0 mt-0.5" />;
    $[12] = isToggling;
    $[13] = isTracked;
    $[14] = onToggleTracking;
    $[15] = trackingEnabled;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className={t2}>{t6}{t7}</div>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

// ---------------------------------------------------------------------------
// BaseCard — shared card wrapper for all styles
// ---------------------------------------------------------------------------

interface BaseCardProps {
  children: React.ReactNode;
  className?: string;
}
function BaseCard(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("rounded-2xl bg-white border border-gray-200 transition-all shadow-sm", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// StageCard — collapsible card for the default timeline style
// ---------------------------------------------------------------------------

function StageCard(t0) {
  const $ = _c(73);
  const {
    component,
    allComponents,
    showComponentSources: t1,
    allSources: t2,
    phaseName,
    initialCollapsed: t3,
    isComponentTracked,
    onToggleTracking,
    isToggling,
    trackingEnabled
  } = t0;
  const showComponentSources = t1 === undefined ? false : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const allSources = t4;
  const initialCollapsed = t3 === undefined ? false : t3;
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  let T0;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== allComponents || $[3] !== allSources || $[4] !== collapsed || $[5] !== component.children || $[6] !== component.content || $[7] !== component.dates || $[8] !== component.id || $[9] !== component.kind || $[10] !== component.name || $[11] !== component.references || $[12] !== component.sources || $[13] !== component.summary || $[14] !== isComponentTracked || $[15] !== isToggling || $[16] !== onToggleTracking || $[17] !== phaseName || $[18] !== showComponentSources || $[19] !== trackingEnabled) {
    const inlineChildren = component.children?.filter(_temp2) ?? [];
    const refChildren = component.children?.filter(_temp3).map(ref => resolveComponent(ref, allComponents)).filter(Boolean) as WorkflowComponent[] ?? [];
    const allChildIds = [...inlineChildren, ...refChildren].map(_temp4);
    const trackedCount = trackingEnabled && isComponentTracked ? allChildIds.filter(id => isComponentTracked(id)).length : 0;
    t8 = "relative group";
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="absolute -left-[37px] md:-left-[45px] top-1 w-5 h-5 md:w-7 md:h-7 rounded-full border-4 border-white z-10 bg-primary-600" />;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    T0 = BaseCard;
    t5 = "hover:border-primary-200 hover:shadow-md";
    let t10;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = () => setCollapsed(_temp5);
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== collapsed) {
      t11 = <span className="flex-shrink-0 mt-1 text-gray-400">{collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>;
      $[28] = collapsed;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== phaseName) {
      t12 = phaseName && <span className="px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-bold uppercase tracking-widest">{phaseName}</span>;
      $[30] = phaseName;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== component.dates) {
      t13 = component.dates?.displayDate && <span className="text-xs font-bold text-primary-600 uppercase tracking-widest">{component.dates.displayDate}</span>;
      $[32] = component.dates;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    let t14;
    if ($[34] !== component.kind) {
      t14 = <span className="px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-gray-500 text-[10px] font-bold uppercase tracking-widest">{component.kind}</span>;
      $[34] = component.kind;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[36] !== t12 || $[37] !== t13 || $[38] !== t14) {
      t15 = <div className="flex items-center gap-3 mb-1">{t12}{t13}{t14}</div>;
      $[36] = t12;
      $[37] = t13;
      $[38] = t14;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== component.name) {
      t16 = <h3 className="text-xl font-bold text-gray-900">{component.name}</h3>;
      $[40] = component.name;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== collapsed || $[43] !== component.summary) {
      t17 = collapsed && component.summary && <p className="text-gray-500 text-sm font-medium mt-1 line-clamp-2">{component.summary}</p>;
      $[42] = collapsed;
      $[43] = component.summary;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
      t18 = <div className="min-w-0 flex-1">{t15}{t16}{t17}</div>;
      $[45] = t15;
      $[46] = t16;
      $[47] = t17;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    let t19;
    if ($[49] !== t11 || $[50] !== t18) {
      t19 = <button type="button" onClick={t10} className="flex-1 flex items-start gap-3 p-6 md:p-8 text-left cursor-pointer">{t11}{t18}</button>;
      $[49] = t11;
      $[50] = t18;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    let t20;
    if ($[52] !== component.id || $[53] !== component.kind || $[54] !== component.name || $[55] !== isComponentTracked || $[56] !== isToggling || $[57] !== onToggleTracking || $[58] !== trackedCount || $[59] !== trackingEnabled) {
      t20 = trackingEnabled && onToggleTracking && <div className="flex-shrink-0 pt-6 pr-6 md:pt-8 md:pr-8 flex items-center gap-2">{trackedCount > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" title={`${trackedCount} item${trackedCount > 1 ? "s" : ""} tracked inside`} />}<TrackComponentButton isTracked={!!isComponentTracked?.(component.id)} onToggle={() => onToggleTracking(component.id, component.kind, component.name)} isToggling={isToggling} size="md" /></div>;
      $[52] = component.id;
      $[53] = component.kind;
      $[54] = component.name;
      $[55] = isComponentTracked;
      $[56] = isToggling;
      $[57] = onToggleTracking;
      $[58] = trackedCount;
      $[59] = trackingEnabled;
      $[60] = t20;
    } else {
      t20 = $[60];
    }
    if ($[61] !== t19 || $[62] !== t20) {
      t6 = <div className="flex items-start">{t19}{t20}</div>;
      $[61] = t19;
      $[62] = t20;
      $[63] = t6;
    } else {
      t6 = $[63];
    }
    t7 = !collapsed && <div className="px-6 md:px-8 pb-6 md:pb-8 pt-0 space-y-5">{component.summary && <p className="text-gray-500 text-sm font-medium">{component.summary}</p>}{component.content && <div className="text-gray-700 text-sm leading-relaxed"><Markdown>{component.content}</Markdown></div>}{inlineChildren.length > 0 && <div className="space-y-2 pt-4 border-t border-gray-100">{inlineChildren.map(child => <InlineChildComponent key={child.id} component={child} isTracked={isComponentTracked?.(child.id)} onToggleTracking={onToggleTracking ? () => onToggleTracking(child.id, child.kind, child.name) : undefined} isToggling={isToggling} trackingEnabled={trackingEnabled} />)}</div>}{refChildren.length > 0 && <div className="space-y-2 pt-4 border-t border-gray-100">{refChildren.map(child_0 => <InlineChildComponent key={child_0.id} component={child_0} isTracked={isComponentTracked?.(child_0.id)} onToggleTracking={onToggleTracking ? () => onToggleTracking(child_0.id, child_0.kind, child_0.name) : undefined} isToggling={isToggling} trackingEnabled={trackingEnabled} />)}</div>}{component.references && component.references.length > 0 && <div className="flex flex-wrap gap-1.5 pt-4 border-t border-gray-100">{component.references.map(_temp6)}</div>}{showComponentSources && component.sources && component.sources.length > 0 && <div className="pt-3 border-t border-gray-100 space-y-1"><div className="flex items-center gap-1 text-xs text-gray-400"><span>Sources</span><ExternalLink className="w-3 h-3" /></div>{component.sources.map(srcRef => {
          const source = allSources.find(s => s.id === srcRef.sourceId);
          if (!source) {
            return null;
          }
          return source.url ? <a key={source.id} href={source.url} target="_blank" rel="noopener noreferrer" className="block text-sm text-primary-600 hover:underline font-medium cursor-pointer">{source.title}</a> : <span key={source.id} className="block text-sm text-gray-500 font-medium">{source.title}</span>;
        })}</div>}</div>;
    $[2] = allComponents;
    $[3] = allSources;
    $[4] = collapsed;
    $[5] = component.children;
    $[6] = component.content;
    $[7] = component.dates;
    $[8] = component.id;
    $[9] = component.kind;
    $[10] = component.name;
    $[11] = component.references;
    $[12] = component.sources;
    $[13] = component.summary;
    $[14] = isComponentTracked;
    $[15] = isToggling;
    $[16] = onToggleTracking;
    $[17] = phaseName;
    $[18] = showComponentSources;
    $[19] = trackingEnabled;
    $[20] = T0;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    T0 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t10;
  if ($[64] !== T0 || $[65] !== t5 || $[66] !== t6 || $[67] !== t7) {
    t10 = <T0 className={t5}>{t6}{t7}</T0>;
    $[64] = T0;
    $[65] = t5;
    $[66] = t6;
    $[67] = t7;
    $[68] = t10;
  } else {
    t10 = $[68];
  }
  let t11;
  if ($[69] !== t10 || $[70] !== t8 || $[71] !== t9) {
    t11 = <div className={t8}>{t9}{t10}</div>;
    $[69] = t10;
    $[70] = t8;
    $[71] = t9;
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  return t11;
}

// ---------------------------------------------------------------------------
// InfographicCard — for the infographic style (alternating left/right)
// ---------------------------------------------------------------------------
function _temp6(ref_0, i) {
  return <span key={`${ref_0.type}-${ref_0.key ?? ref_0.id ?? i}`} className="px-2 py-1 rounded-lg bg-primary-50 border border-primary-100 text-[10px] text-primary-700 font-bold">{ref_0.name ?? ref_0.key ?? `${ref_0.type}/${ref_0.id}`}</span>;
}
function _temp5(v) {
  return !v;
}
function _temp4(c_1) {
  return c_1.id;
}
function _temp3(c_0) {
  return isComponentRef(c_0);
}
function _temp2(c) {
  return isInlineComponent(c);
}
function InfographicCard(t0) {
  const $ = _c(56);
  const {
    component,
    allComponents,
    showComponentSources: t1,
    allSources: t2,
    initialCollapsed: t3,
    phaseName,
    phaseLabelPosition: t4,
    isComponentTracked,
    onToggleTracking,
    isToggling,
    trackingEnabled
  } = t0;
  const showComponentSources = t1 === undefined ? false : t1;
  let t5;
  if ($[0] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const allSources = t5;
  const initialCollapsed = t3 === undefined ? false : t3;
  const phaseLabelPosition = t4 === undefined ? "above" : t4;
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  let t6;
  if ($[2] !== allComponents || $[3] !== allSources || $[4] !== collapsed || $[5] !== component.children || $[6] !== component.content || $[7] !== component.id || $[8] !== component.kind || $[9] !== component.name || $[10] !== component.sources || $[11] !== component.summary || $[12] !== isComponentTracked || $[13] !== isToggling || $[14] !== onToggleTracking || $[15] !== phaseLabelPosition || $[16] !== phaseName || $[17] !== showComponentSources || $[18] !== trackingEnabled) {
    const inlineChildren = component.children?.filter(_temp7) ?? [];
    const refChildren = component.children?.filter(_temp8).map(ref => resolveComponent(ref, allComponents)).filter(Boolean) as WorkflowComponent[] ?? [];
    const allChildIds = [...inlineChildren, ...refChildren].map(_temp9);
    const trackedCount = trackingEnabled && isComponentTracked ? allChildIds.filter(id => isComponentTracked(id)).length : 0;
    let t7;
    if ($[20] !== phaseName) {
      t7 = phaseName ? <span className="inline-block px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-bold uppercase tracking-widest">{phaseName}</span> : null;
      $[20] = phaseName;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    const phaseBadge = t7;
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = () => setCollapsed(_temp10);
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== collapsed) {
      t9 = <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors">{collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>;
      $[23] = collapsed;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== phaseBadge || $[26] !== phaseLabelPosition) {
      t10 = phaseLabelPosition === "above" && phaseBadge && <div className="mb-1">{phaseBadge}</div>;
      $[25] = phaseBadge;
      $[26] = phaseLabelPosition;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== component.name) {
      t11 = <h4 className="text-sm font-bold text-gray-900 leading-tight">{component.name}</h4>;
      $[28] = component.name;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== phaseBadge || $[31] !== phaseLabelPosition) {
      t12 = phaseLabelPosition === "below" && phaseBadge && <div className="mt-1">{phaseBadge}</div>;
      $[30] = phaseBadge;
      $[31] = phaseLabelPosition;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== collapsed || $[34] !== component.summary) {
      t13 = collapsed && component.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{component.summary}</p>;
      $[33] = collapsed;
      $[34] = component.summary;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13) {
      t14 = <div className="min-w-0 flex-1">{t10}{t11}{t12}{t13}</div>;
      $[36] = t10;
      $[37] = t11;
      $[38] = t12;
      $[39] = t13;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    let t15;
    if ($[41] !== t14 || $[42] !== t9) {
      t15 = <button type="button" onClick={t8} className="flex-1 flex items-start gap-2 p-4 text-left cursor-pointer">{t9}{t14}</button>;
      $[41] = t14;
      $[42] = t9;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    let t16;
    if ($[44] !== component.id || $[45] !== component.kind || $[46] !== component.name || $[47] !== isComponentTracked || $[48] !== isToggling || $[49] !== onToggleTracking || $[50] !== trackedCount || $[51] !== trackingEnabled) {
      t16 = trackingEnabled && onToggleTracking && <div className="flex-shrink-0 pt-4 pr-4 flex items-center gap-1.5">{trackedCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" title={`${trackedCount} item${trackedCount > 1 ? "s" : ""} tracked inside`} />}<TrackComponentButton isTracked={!!isComponentTracked?.(component.id)} onToggle={() => onToggleTracking(component.id, component.kind, component.name)} isToggling={isToggling} size="sm" /></div>;
      $[44] = component.id;
      $[45] = component.kind;
      $[46] = component.name;
      $[47] = isComponentTracked;
      $[48] = isToggling;
      $[49] = onToggleTracking;
      $[50] = trackedCount;
      $[51] = trackingEnabled;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    let t17;
    if ($[53] !== t15 || $[54] !== t16) {
      t17 = <div className="flex items-start">{t15}{t16}</div>;
      $[53] = t15;
      $[54] = t16;
      $[55] = t17;
    } else {
      t17 = $[55];
    }
    t6 = <BaseCard className="hover:shadow-md w-full">{t17}{!collapsed && <div className="px-4 pb-4 pt-0 space-y-3">{component.summary && <p className="text-sm text-gray-500">{component.summary}</p>}{component.content && <div className="text-gray-700 text-xs leading-relaxed"><Markdown className="text-sm [&_p]:text-sm [&_li]:text-sm [&_p]:mb-1">{component.content}</Markdown></div>}{inlineChildren.length > 0 && <div className="space-y-1.5 pt-2 border-t border-gray-100">{inlineChildren.map(child => <InlineChildComponent key={child.id} component={child} isTracked={isComponentTracked?.(child.id)} onToggleTracking={onToggleTracking ? () => onToggleTracking(child.id, child.kind, child.name) : undefined} isToggling={isToggling} trackingEnabled={trackingEnabled} />)}</div>}{refChildren.length > 0 && <div className="space-y-1.5 pt-2 border-t border-gray-100">{refChildren.map(child_0 => <InlineChildComponent key={child_0.id} component={child_0} isTracked={isComponentTracked?.(child_0.id)} onToggleTracking={onToggleTracking ? () => onToggleTracking(child_0.id, child_0.kind, child_0.name) : undefined} isToggling={isToggling} trackingEnabled={trackingEnabled} />)}</div>}{showComponentSources && component.sources && component.sources.length > 0 && <div className="pt-2 border-t border-gray-100 space-y-0.5"><div className="flex items-center gap-1 text-xs text-gray-400"><span>Sources</span><ExternalLink className="w-3 h-3" /></div>{component.sources.map(srcRef => {
            const source = allSources.find(s => s.id === srcRef.sourceId);
            if (!source) {
              return null;
            }
            return source.url ? <a key={source.id} href={source.url} target="_blank" rel="noopener noreferrer" className="block text-xs text-primary-600 hover:underline font-medium cursor-pointer">{source.title}</a> : <span key={source.id} className="block text-xs text-gray-500 font-medium">{source.title}</span>;
          })}</div>}</div>}</BaseCard>;
    $[2] = allComponents;
    $[3] = allSources;
    $[4] = collapsed;
    $[5] = component.children;
    $[6] = component.content;
    $[7] = component.id;
    $[8] = component.kind;
    $[9] = component.name;
    $[10] = component.sources;
    $[11] = component.summary;
    $[12] = isComponentTracked;
    $[13] = isToggling;
    $[14] = onToggleTracking;
    $[15] = phaseLabelPosition;
    $[16] = phaseName;
    $[17] = showComponentSources;
    $[18] = trackingEnabled;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Default Timeline
// ---------------------------------------------------------------------------
function _temp10(v) {
  return !v;
}
function _temp9(c_1) {
  return c_1.id;
}
function _temp8(c_0) {
  return isComponentRef(c_0);
}
function _temp7(c) {
  return isInlineComponent(c);
}
function WorkflowTimeline(t0) {
  const $ = _c(33);
  const {
    view,
    allComponents,
    showPhases,
    showComponentSources,
    allSources,
    collapseConfig,
    isComponentTracked,
    onToggleTracking,
    isToggling,
    trackingEnabled
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== allComponents || $[1] !== allSources || $[2] !== collapseConfig || $[3] !== isComponentTracked || $[4] !== isToggling || $[5] !== onToggleTracking || $[6] !== showComponentSources || $[7] !== showPhases || $[8] !== trackingEnabled || $[9] !== view.componentRefs) {
    let t4;
    if ($[13] !== allComponents) {
      t4 = ref => resolveComponent(ref, allComponents);
      $[13] = allComponents;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const resolvedComponents = view.componentRefs.map(t4).filter(Boolean) as WorkflowComponent[];
    const visibleComponents = showPhases ? resolvedComponents : resolvedComponents.filter(_temp11);
    let componentPhaseMap;
    if ($[15] !== allComponents) {
      const phaseComponents = allComponents.filter(_temp12);
      componentPhaseMap = new Map();
      for (const phase of phaseComponents) {
        if (phase.children) {
          for (const child of phase.children) {
            if (isComponentRef(child)) {
              componentPhaseMap.set(child.componentId, phase.name);
            }
          }
        }
      }
      $[15] = allComponents;
      $[16] = componentPhaseMap;
    } else {
      componentPhaseMap = $[16];
    }
    t3 = "space-y-8";
    t1 = "relative pl-4 md:pl-6 space-y-10 before:absolute before:left-[8px] md:before:left-[8px] before:top-4 before:bottom-4 before:w-px before:bg-gradient-to-b before:from-primary-600 before:via-gray-200 before:to-transparent";
    let t5;
    if ($[17] !== allComponents || $[18] !== allSources || $[19] !== collapseConfig || $[20] !== componentPhaseMap || $[21] !== isComponentTracked || $[22] !== isToggling || $[23] !== onToggleTracking || $[24] !== showComponentSources || $[25] !== trackingEnabled) {
      t5 = component => <StageCard key={component.id} component={component} allComponents={allComponents} showComponentSources={showComponentSources} allSources={allSources} phaseName={componentPhaseMap.get(component.id)} initialCollapsed={getInitialCollapsed(component.id, collapseConfig)} isComponentTracked={isComponentTracked} onToggleTracking={onToggleTracking} isToggling={isToggling} trackingEnabled={trackingEnabled} />;
      $[17] = allComponents;
      $[18] = allSources;
      $[19] = collapseConfig;
      $[20] = componentPhaseMap;
      $[21] = isComponentTracked;
      $[22] = isToggling;
      $[23] = onToggleTracking;
      $[24] = showComponentSources;
      $[25] = trackingEnabled;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    t2 = visibleComponents.map(t5);
    $[0] = allComponents;
    $[1] = allSources;
    $[2] = collapseConfig;
    $[3] = isComponentTracked;
    $[4] = isToggling;
    $[5] = onToggleTracking;
    $[6] = showComponentSources;
    $[7] = showPhases;
    $[8] = trackingEnabled;
    $[9] = view.componentRefs;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
  }
  let t4;
  if ($[27] !== t1 || $[28] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  let t5;
  if ($[30] !== t3 || $[31] !== t4) {
    t5 = <section className={t3}>{t4}</section>;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// Infographic Timeline — centered vertical line with alternating cards
// ---------------------------------------------------------------------------
function _temp12(c_0) {
  return c_0.kind === "phase";
}
function _temp11(c) {
  return c.kind !== "phase";
}
function InfographicTimeline(t0) {
  const $ = _c(32);
  const {
    view,
    allComponents,
    showPhases,
    showComponentSources,
    allSources,
    collapseConfig,
    forceMobile: t1,
    showPhaseLabel: t2,
    phaseLabelPosition: t3,
    isComponentTracked,
    onToggleTracking,
    isToggling,
    trackingEnabled
  } = t0;
  const forceMobile = t1 === undefined ? false : t1;
  const showPhaseLabel = t2 === undefined ? false : t2;
  const phaseLabelPosition = t3 === undefined ? "above" : t3;
  let t4;
  if ($[0] !== allComponents || $[1] !== allSources || $[2] !== collapseConfig || $[3] !== forceMobile || $[4] !== isComponentTracked || $[5] !== isToggling || $[6] !== onToggleTracking || $[7] !== phaseLabelPosition || $[8] !== showComponentSources || $[9] !== showPhaseLabel || $[10] !== showPhases || $[11] !== trackingEnabled || $[12] !== view.componentRefs) {
    let t5;
    if ($[14] !== allComponents) {
      t5 = ref => resolveComponent(ref, allComponents);
      $[14] = allComponents;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    const resolvedComponents = view.componentRefs.map(t5).filter(Boolean) as WorkflowComponent[];
    const visibleComponents = showPhases ? resolvedComponents : resolvedComponents.filter(_temp13);
    let t6;
    if ($[16] !== allComponents || $[17] !== showPhaseLabel) {
      bb0: {
        const map = new Map();
        if (!showPhaseLabel) {
          t6 = map;
          break bb0;
        }
        const phaseComponents = allComponents.filter(_temp14);
        for (const phase of phaseComponents) {
          if (phase.children) {
            for (const child of phase.children) {
              if (isComponentRef(child)) {
                map.set(child.componentId, phase.name);
              }
            }
          }
        }
        t6 = map;
      }
      $[16] = allComponents;
      $[17] = showPhaseLabel;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    const componentPhaseMap = t6;
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-amber-400" />;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== allComponents || $[21] !== allSources || $[22] !== collapseConfig || $[23] !== componentPhaseMap || $[24] !== isComponentTracked || $[25] !== isToggling || $[26] !== onToggleTracking || $[27] !== phaseLabelPosition || $[28] !== showComponentSources || $[29] !== showPhaseLabel || $[30] !== trackingEnabled) {
      t8 = component => {
        const dateLabel = component.dates?.displayDate;
        return <div key={component.id} className="relative flex items-start gap-4 pl-8"><div className="absolute left-1.5 top-3 w-3.5 h-3.5 rounded-full bg-amber-400 border-[3px] border-white shadow-sm z-10" /><div className="flex-1 space-y-1">{dateLabel && <div className="inline-block px-2 py-1 rounded-md bg-white border-2 border-amber-400 shadow-sm mb-2"><span className="text-[11px] font-black text-gray-900 leading-tight whitespace-nowrap">{dateLabel}</span></div>}<InfographicCard component={component} allComponents={allComponents} showComponentSources={showComponentSources} allSources={allSources} side="right" initialCollapsed={getInitialCollapsed(component.id, collapseConfig)} phaseName={showPhaseLabel ? componentPhaseMap.get(component.id) : undefined} phaseLabelPosition={phaseLabelPosition} isComponentTracked={isComponentTracked} onToggleTracking={onToggleTracking} isToggling={isToggling} trackingEnabled={trackingEnabled} /></div></div>;
      };
      $[20] = allComponents;
      $[21] = allSources;
      $[22] = collapseConfig;
      $[23] = componentPhaseMap;
      $[24] = isComponentTracked;
      $[25] = isToggling;
      $[26] = onToggleTracking;
      $[27] = phaseLabelPosition;
      $[28] = showComponentSources;
      $[29] = showPhaseLabel;
      $[30] = trackingEnabled;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    t4 = <section className="relative"><div className={forceMobile ? "" : "md:hidden"}>{t7}<div className="space-y-8">{visibleComponents.map(t8)}</div></div>{!forceMobile && <div className="hidden md:block relative"><div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-amber-400 -translate-x-1/2" /><div className="space-y-12">{visibleComponents.map((component_0, index) => {
            const side = index % 2 === 0 ? "left" : "right";
            const dateLabel_0 = component_0.dates?.displayDate;
            return <div key={component_0.id} className="relative flex items-start"><div className="w-[calc(50%-2.5rem)] flex justify-end pr-6">{side === "left" && <InfographicCard component={component_0} allComponents={allComponents} showComponentSources={showComponentSources} allSources={allSources} side="left" initialCollapsed={getInitialCollapsed(component_0.id, collapseConfig)} phaseName={showPhaseLabel ? componentPhaseMap.get(component_0.id) : undefined} phaseLabelPosition={phaseLabelPosition} isComponentTracked={isComponentTracked} onToggleTracking={onToggleTracking} isToggling={isToggling} trackingEnabled={trackingEnabled} />}</div><div className="relative flex flex-col items-center z-10 flex-shrink-0">{dateLabel_0 ? <div className="px-2.5 py-2 rounded-lg bg-white border-2 border-amber-400 flex items-center justify-center shadow-sm"><span className="text-xs font-black text-gray-900 text-center leading-tight whitespace-nowrap">{dateLabel_0}</span></div> : <div className="w-4 h-4 rounded-full bg-amber-400 border-4 border-white shadow-sm" />}<div className={cn("absolute top-1/2 -translate-y-1/2 w-6 h-0.5 bg-amber-400", side === "left" ? "right-full" : "left-full")} /></div><div className="w-[calc(50%-2.5rem)] flex justify-start pl-6">{side === "right" && <InfographicCard component={component_0} allComponents={allComponents} showComponentSources={showComponentSources} allSources={allSources} side="right" initialCollapsed={getInitialCollapsed(component_0.id, collapseConfig)} phaseName={showPhaseLabel ? componentPhaseMap.get(component_0.id) : undefined} phaseLabelPosition={phaseLabelPosition} isComponentTracked={isComponentTracked} onToggleTracking={onToggleTracking} isToggling={isToggling} trackingEnabled={trackingEnabled} />}</div></div>;
          })}</div></div>}</section>;
    $[0] = allComponents;
    $[1] = allSources;
    $[2] = collapseConfig;
    $[3] = forceMobile;
    $[4] = isComponentTracked;
    $[5] = isToggling;
    $[6] = onToggleTracking;
    $[7] = phaseLabelPosition;
    $[8] = showComponentSources;
    $[9] = showPhaseLabel;
    $[10] = showPhases;
    $[11] = trackingEnabled;
    $[12] = view.componentRefs;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function _temp14(c_0) {
  return c_0.kind === "phase";
}
function _temp13(c) {
  return c.kind !== "phase";
}
export function WorkflowDetailView(t0) {
  const $ = _c(62);
  const {
    workflow,
    embedded: t1,
    showPhases: t2,
    showComponentSources: t3,
    showSidebar: t4,
    variant: t5,
    collapseConfig,
    forceMobileLayout: t6,
    showPhaseLabel: t7,
    phaseLabelPosition: t8,
    trackingObjectId,
    trackingObjectType,
    showTrackingKey
  } = t0;
  const embedded = t1 === undefined ? false : t1;
  const showPhases = t2 === undefined ? false : t2;
  const showComponentSources = t3 === undefined ? false : t3;
  const showSidebar = t4 === undefined ? false : t4;
  const variant = t5 === undefined ? "default" : t5;
  const forceMobileLayout = t6 === undefined ? false : t6;
  const showPhaseLabel = t7 === undefined ? false : t7;
  const phaseLabelPosition = t8 === undefined ? "above" : t8;
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const activeView = workflow.views[activeViewIndex];
  const trackingEnabled = !!trackingObjectId && !!trackingObjectType;
  const {
    isComponentTracked,
    toggleTracking,
    isToggling
  } = useComponentTracking(trackingObjectId ?? 0, trackingObjectType ?? "");
  const t9 = embedded ? "space-y-12" : "max-w-5xl mx-auto space-y-12 pb-24 px-4 sm:px-6 lg:px-8 pt-8";
  let t10;
  if ($[0] !== embedded || $[1] !== workflow.description || $[2] !== workflow.tags || $[3] !== workflow.title) {
    t10 = !embedded && <div className="space-y-6"><div className="space-y-4">{workflow.tags && workflow.tags.length > 0 && <div className="flex flex-wrap items-center gap-2">{workflow.tags.map(_temp15)}</div>}<h1 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900 leading-tight">{workflow.title}</h1>{workflow.description && <p className="text-xl md:text-2xl font-medium text-gray-500">{workflow.description}</p>}</div></div>;
    $[0] = embedded;
    $[1] = workflow.description;
    $[2] = workflow.tags;
    $[3] = workflow.title;
    $[4] = t10;
  } else {
    t10 = $[4];
  }
  let t11;
  if ($[5] !== activeViewIndex || $[6] !== workflow.views) {
    t11 = workflow.views.length > 1 && <div className="flex flex-wrap gap-2">{workflow.views.map((view, index) => <button key={view.id} onClick={() => setActiveViewIndex(index)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${index === activeViewIndex ? "bg-primary-600 text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{view.name}</button>)}</div>;
    $[5] = activeViewIndex;
    $[6] = workflow.views;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  let t12;
  if ($[8] !== activeView) {
    t12 = activeView?.description && <p className="text-gray-600 text-sm font-medium italic">{activeView.description}</p>;
    $[8] = activeView;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const t13 = `grid grid-cols-1 ${showSidebar ? "lg:grid-cols-3" : ""} gap-12`;
  const t14 = `${showSidebar ? "lg:col-span-2" : ""} space-y-6`;
  let t15;
  if ($[10] !== showSidebar || $[11] !== workflow) {
    t15 = showSidebar && <div className="lg:hidden"><WorkflowSidebar workflow={workflow} /></div>;
    $[10] = showSidebar;
    $[11] = workflow;
    $[12] = t15;
  } else {
    t15 = $[12];
  }
  let t16;
  if ($[13] !== showTrackingKey || $[14] !== trackingEnabled) {
    t16 = trackingEnabled && showTrackingKey !== false && <div className="flex items-center gap-4 text-xs text-gray-500"><span className="flex items-center gap-2"><Bookmark className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /><span>Bookmark items to track, follow up later</span></span><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /><span>Items tracked inside</span></span></div>;
    $[13] = showTrackingKey;
    $[14] = trackingEnabled;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== activeView || $[17] !== collapseConfig || $[18] !== forceMobileLayout || $[19] !== isComponentTracked || $[20] !== isToggling || $[21] !== phaseLabelPosition || $[22] !== showComponentSources || $[23] !== showPhaseLabel || $[24] !== showPhases || $[25] !== toggleTracking || $[26] !== trackingEnabled || $[27] !== variant || $[28] !== workflow.components || $[29] !== workflow.sources) {
    t17 = activeView && variant === "infographic" && <InfographicTimeline view={activeView} allComponents={workflow.components} showPhases={showPhases} showComponentSources={showComponentSources} allSources={workflow.sources} collapseConfig={collapseConfig} forceMobile={forceMobileLayout} showPhaseLabel={showPhaseLabel} phaseLabelPosition={phaseLabelPosition} isComponentTracked={trackingEnabled ? isComponentTracked : undefined} onToggleTracking={trackingEnabled ? toggleTracking : undefined} isToggling={isToggling} trackingEnabled={trackingEnabled} />;
    $[16] = activeView;
    $[17] = collapseConfig;
    $[18] = forceMobileLayout;
    $[19] = isComponentTracked;
    $[20] = isToggling;
    $[21] = phaseLabelPosition;
    $[22] = showComponentSources;
    $[23] = showPhaseLabel;
    $[24] = showPhases;
    $[25] = toggleTracking;
    $[26] = trackingEnabled;
    $[27] = variant;
    $[28] = workflow.components;
    $[29] = workflow.sources;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== activeView || $[32] !== collapseConfig || $[33] !== isComponentTracked || $[34] !== isToggling || $[35] !== showComponentSources || $[36] !== showPhases || $[37] !== toggleTracking || $[38] !== trackingEnabled || $[39] !== variant || $[40] !== workflow.components || $[41] !== workflow.sources) {
    t18 = activeView && variant === "default" && <WorkflowTimeline view={activeView} allComponents={workflow.components} showPhases={showPhases} showComponentSources={showComponentSources} allSources={workflow.sources} collapseConfig={collapseConfig} isComponentTracked={trackingEnabled ? isComponentTracked : undefined} onToggleTracking={trackingEnabled ? toggleTracking : undefined} isToggling={isToggling} trackingEnabled={trackingEnabled} />;
    $[31] = activeView;
    $[32] = collapseConfig;
    $[33] = isComponentTracked;
    $[34] = isToggling;
    $[35] = showComponentSources;
    $[36] = showPhases;
    $[37] = toggleTracking;
    $[38] = trackingEnabled;
    $[39] = variant;
    $[40] = workflow.components;
    $[41] = workflow.sources;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t14 || $[44] !== t15 || $[45] !== t16 || $[46] !== t17 || $[47] !== t18) {
    t19 = <div className={t14}>{t15}{t16}{t17}{t18}</div>;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== showSidebar || $[50] !== workflow) {
    t20 = showSidebar && <div className="hidden lg:block"><WorkflowSidebar workflow={workflow} /></div>;
    $[49] = showSidebar;
    $[50] = workflow;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t13 || $[53] !== t19 || $[54] !== t20) {
    t21 = <div className={t13}>{t19}{t20}</div>;
    $[52] = t13;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t10 || $[57] !== t11 || $[58] !== t12 || $[59] !== t21 || $[60] !== t9) {
    t22 = <div className={t9}>{t10}{t11}{t12}{t21}</div>;
    $[56] = t10;
    $[57] = t11;
    $[58] = t12;
    $[59] = t21;
    $[60] = t9;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  return t22;
}
function _temp15(tag) {
  return <span key={tag} className="px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-600 text-[10px] font-bold uppercase tracking-widest">{tag}</span>;
}